import React, {FC, useEffect, useState, useRef, MutableRefObject} from "react";
import { useForm } from 'react-hook-form';
import {useQuery, useMutation} from 'react-query';
import { AxiosError } from "axios";
import {
  Button,
  Dialog,
  FormControl,
  FormControlLabel,
  FormHelperText,
  IconButton,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Radio,
  RadioGroup,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
  Typography
} from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import useSession from "../../hooks/user/useSession";
import stateOptions from '../../lib/US_STATES'
import orgAPI from "../../apis/orgAPI";
import herdAPI from "../../apis/herdAPI";
import smartyAPI from "../../apis/external/SmartyAPI";
import { verifySmartyResponse } from "../../util/smartyAPIHelper";
import { PulseLoader } from "react-spinners";

import {
  HerdLocationObject,
  HerdOrgsObject,
} from '../../herdportalTypes';
import TextMaskCustom from '../../components/TextMaskCustom';
import useAlert from "../../hooks/alert/useAlert";
import {useResponsive} from "../../hooks/style";

export interface CreateHerdFormState extends HerdLocationObject, HerdOrgsObject {
  code: string;
  farm_name: string | null;
  owner_name: string | null;
  species_code: number | null;
}

type CreateHerdDialogProps = {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  onError: (msg: string) => void;
};

const defaultFormState = {
  code: '',
  owner_name: '',
  farm_name: '',
  species_code: 0,
  street_address: '',
  city: '',
  state_code: '',
  zip_code: '',
  drpc_code: null,
  fsp_code: null
};

const speciesOptions = [{ code: 0, species: 'Cow' }, { code: 1, species: 'Goat' }]; // DO NOT CHANGE THIS IS THE ONLY HARD REFERENCE TO INT

const CreateHerdDialog: FC<CreateHerdDialogProps> = ({ open, onClose, onSuccess }) => {
  const alert = useAlert();
  const { handleSubmit } = useForm({ mode: 'onSubmit', defaultValues: defaultFormState });
  const { userInfo: { orgId: userOrgId, orgTypeAbbrev, orgCode } } = useSession();
  const herdCode_ref: MutableRefObject<HTMLInputElement | undefined> = useRef();
  const {isDesktop, isPhone} = useResponsive();

  const [FSPOptions, setFSPOptions] = useState<Record<string, string>[]>([]);
  const [DRPCOptions, setDRPCOptions] = useState<Record<string, string>[]>([]);
  const [formState, setFormState] = useState<CreateHerdFormState>(defaultFormState);
  const [autoAssignHerdCode, setAutoAssignHerdCode] = useState("true");
  const [saving, setSaving] = useState(false);
  const [herdCodeError, setHerdCodeError] = useState(false);
  const [herdCodeHelperText, setHerdCodeHelperText] = useState('');
  const [zipCodeError, setZipCodeError] = useState(false);
  const [zipCodeHelperText, setZipCodeHelperText] = useState('');

  const foundOrgs = useQuery('allOrgs_createHerd', () => orgAPI.getAll(), {staleTime: Infinity});

  const handleInputChange = (event) => {
    const {target: {name, value}} = event;

    setFormState({...formState, [name]: value});

    if (name === 'code' && value.length === 10)
      setHerdCodeError(false);

    if (name === 'zip_code' && value.length >= 5)
      setZipCodeError(false);
  };

  const handleSelectChange = (event: SelectChangeEvent) => {
    const { target: { name, value } } = event;
    const changed = { ...formState, [name]: value };
    setFormState({ ...formState, [name]: value });
  }

  const handleAutoAssignSelect = (value) => {
    setAutoAssignHerdCode(value);
  };

  const handleClose = () => {
    resetFormState();
    onClose();
  };

  const handleBlur = (event) => {
    const {target: {name, value}} = event;

    if (name === 'code' && value.length < 10) {
      setHerdCodeError(true);
      setHerdCodeHelperText('Herd code must have 8 digits');
    }

    if (name === 'zip_code' && value.length < 5) {
      setZipCodeError(true);
      setZipCodeHelperText('Zip code must have at least 5 digits');
    }
  };

  const resetFormState = () => setFormState(defaultFormState);

  const onSubmit = async () => {
    if (herdCodeError || zipCodeError)
      return;

    // First validate the address
    const addressInfo = {
      country: "US",
      administrativeArea: formState.state_code || '',
      locality: encodeURIComponent(formState.city),
      address1: encodeURIComponent(formState.street_address),
      postalCode: formState.zip_code || ''
    };

    const smartyResponse = await smartyAPI.verifyUSAddress(addressInfo);

    if (!smartyResponse) {
      alert.error(`Invalid address information entered, please check the address fields.`);
    }
    else {
      const error = verifySmartyResponse(smartyResponse);

      if (!!error) {
        let msg = "Invalid address information entered: " + error;
        alert.error(msg);
      }
      else {
        setSaving(true);
        addHerd.mutate({fips_code: smartyResponse.metadata?.county_fips, county_name: smartyResponse.metadata?.county_name});
      }
    }
  };

  const buildHerdRequest = (fips_code: string, county_name: string) => {
    const newCode = formState.code.replaceAll('-', '');

    return {
      info: {
        code: parseInt(newCode),
        owner_name: formState.owner_name,
        farm_name: formState.farm_name,
        species_code: formState.species_code
      },
      location: {
        street_address: formState.street_address,
        city: formState.city,
        state_code: formState.state_code,
        zip_code: formState.zip_code,
        fips_code,
        county_name
      },
      orgs: {
        fsp_code: orgTypeAbbrev === 'FSP' ?
          FSPOptions.find(fsp => fsp.id === userOrgId).code :
          formState.fsp_code === '' ?
            null :
            formState.fsp_code,
        drpc_code: orgTypeAbbrev === 'DRPC' ?
          DRPCOptions.find(drpc => drpc.id === userOrgId).code :
          formState.drpc_code === '' ?
            null :
            formState.drpc_code
      }
    }
  };

  const addHerd = useMutation(
    ({fips_code, county_name}: {fips_code: string; county_name: string}) => herdAPI.create(buildHerdRequest(fips_code, county_name)),
    {
      onSuccess: (result: any) => {
        const code = result.created_herd_code?.toString();
        const formattedCode = code.substring(0, 2) + '-' + code.substring(2, 4) + '-' + code.substring(4, 8);
        alert.success(`Herd created successfuly, new Herd Code is:  ${formattedCode}`);
        resetFormState();
        setSaving(false);
        onSuccess();
      },
      onError: (error: AxiosError) => {
        const err = error.response?.data.message;
        if (err.startsWith('Herd already exists')) {
          alert.error(`Herd already exists with Herd Code (${formState.code}), enter a different Code.`);
          if (herdCode_ref?.current) herdCode_ref.current.focus();
        } else if (err.toLowerCase().startsWith('invalid or missing fips code')) {
          setAutoAssignHerdCode("false");
          alert.error(`Herd code could not be auto-generated due to missing FIPS code mapping. Enter herd code manually.`);
          if (herdCode_ref?.current) herdCode_ref.current.focus();
        } else {
          alert.error(err);
        }
        setSaving(false);
      }
    }
  );

  useEffect(() => {
    const allOrgs = foundOrgs.data;
    setFSPOptions(allOrgs?.filter((orgObj) => orgObj.org_type_abbrev === 'FSP'));
    setDRPCOptions(allOrgs?.filter((orgObj) => orgObj.org_type_abbrev === 'DRPC'));
  }, [foundOrgs.data]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      sx={isPhone ? {} : {p: '1rem'}}
      fullScreen={isPhone}
      slotProps={{
        paper: {
          sx: isPhone ? {} : {p: '3rem'}
        }
      }}
    >
      <form onSubmit={handleSubmit((data) => onSubmit())} >
        <Stack
          direction='column'
          spacing={2.5}
          sx={{p: isPhone ? 2 : 0, alignItems: 'left', width: isPhone ? null : (theme) => theme.breakpoints.values.sm}}
        >
          <Stack direction='row' sx={{justifyContent: 'space-between', alignItems: 'center'}}>
            <Typography variant='h5'>Create New Herd</Typography>
            {
              isPhone &&
              <IconButton onClick={handleClose}>
                <CloseIcon/>
              </IconButton>
            }
          </Stack>

          <Stack direction='row' spacing={2} sx={{alignItems: 'center'}}>
            <Typography variant='h6' sx={{mr: '.5rem'}}>Automatically assign a Herd Code?</Typography>
            <RadioGroup
              row
              aria-label="Option"
              name="option_assign_herd_code"
              value={autoAssignHerdCode}
              onChange={(event) => handleAutoAssignSelect(event.target.value)}
            >
              <FormControlLabel value="true" control={<Radio />} label="Yes" />
              <FormControlLabel value="false" control={<Radio />} label="No" />
            </RadioGroup>
          </Stack>

          {autoAssignHerdCode === 'false' &&
            <FormControl
              required
              sx={{width: {xs: '100%', sm: '80%'}}}
            >
              <InputLabel>Herd Code</InputLabel>
              <OutlinedInput
                name='code'
                label='Herd Code'
                value={formState.code}
                onChange={handleInputChange}
                inputComponent={TextMaskCustom as any}
                inputProps={{mask: '00-00-0000', eager: true}}
                onBlur={handleBlur}
                error={herdCodeError}
              />
              {herdCodeError &&
                <FormHelperText error>
                  {herdCodeHelperText}
                </FormHelperText>}
            </FormControl>}

          <TextField
            sx={{ width: { xs: '100%', sm: '80%' } }}
            id="owner_name_input"
            required
            name="owner_name"
            label="Owner Name"
            value={formState.owner_name}
            placeholder='Owner name'
            onChange={handleInputChange}
          />

          <TextField
            sx={{ width: { xs: '100%', sm: '80%' } }}
            id="herd-farm-name-input"
            required
            name="farm_name"
            label="Farm Name"
            value={formState.farm_name}
            onChange={handleInputChange}
            fullWidth
          />

          <FormControl
            required
            sx={{ width: { xs: '100%', sm: '40%' } }}
          >
            <InputLabel id="herd-species-select-label">Species</InputLabel>
            <Select
              labelId="herd-species-select-label"
              id="herd-species-select"
              name="species_code"
              value={formState.species_code !== null ? formState.species_code.toString() : ''}
              label="Species"
              onChange={handleSelectChange}
            >
              <MenuItem value="">No Species Selected</MenuItem>
              {speciesOptions?.map((option, idx) => (
                <MenuItem key={option.species} value={idx}>{option.species}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <FormControl
              disabled={orgTypeAbbrev === 'FSP'}
              sx={{ width: { xs: '100%', sm: '50%' } }}
            >
              <InputLabel id="herd-FSP-select-label">FSA</InputLabel>
              <Select
                labelId="herd-FSP-select-label"
                id="herd-FSP-select"
                name="fsp_code"
                value={orgTypeAbbrev === 'FSP' ? orgCode : formState.fsp_code || ''}
                label="FSA"
                onChange={handleSelectChange}
              >
                <MenuItem value="">No FSP Selected</MenuItem>
                {FSPOptions?.map((option) => (
                  <MenuItem key={option.code} value={option.code}>{option.name}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl
              disabled={orgTypeAbbrev === 'DRPC'}
              sx={{ width: { xs: '100%', sm: '50%' } }}
            >
              <InputLabel id="herd-DRPC-select-label" shrink>DRPC</InputLabel>
              <Select
                labelId="herd-DRPC-select-label"
                id="herd-DRPC-select"
                name="drpc_code"
                value={orgTypeAbbrev === 'DRPC' ? orgCode : formState.drpc_code || ''}
                label="DRPC"
                displayEmpty
                onChange={handleSelectChange}
              >
                <MenuItem value="">Non-DRPC Herd</MenuItem>
                {DRPCOptions?.map((option) => (
                  <MenuItem key={option.code} value={option.code}>{option.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>

          <TextField
            sx={{ width: { xs: '100%', sm: '75%' } }}
            id="herd-streetAddress-input"
            required
            name="street_address"
            label="Street Address"
            value={formState.street_address}
            placeholder='1234 Old Farm Rd.'
            onChange={handleInputChange}
          />

          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <TextField
              id="herd-city-input"
              required
              name="city"
              label="City"
              value={formState.city}
              placeholder='Pleasant View'
              onChange={handleInputChange}
              sx={{ width: { xs: '100%', sm: '55%' } }}
            />

            <FormControl required sx={{ width: { xs: '100%', sm: '20%' } }}>
              <InputLabel id="herd-state-select-label">State</InputLabel>
              <Select
                labelId="herd-state-select-label"
                id="herd-state-select"
                name="state_code"
                label="State"
                value={formState.state_code}
                onChange={handleSelectChange}
              >
                <MenuItem value="">No Selection</MenuItem>
                {stateOptions?.map((option) => (
                  <MenuItem key={option} value={option}>{option}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl
              required
              sx={{width: {xs: '100%', sm: '25%'}}}
            >
              <InputLabel>Zip Code</InputLabel>
              <OutlinedInput
                name='zip_code'
                label='Zip Code'
                value={formState.zip_code}
                placeholder='85164'
                onChange={handleInputChange}
                inputComponent={TextMaskCustom as any}
                inputProps={{mask: '00000-0000', eager: 'remove'}}
                onBlur={handleBlur}
                error={zipCodeError}
              />
              {zipCodeError &&
                <FormHelperText error>
                  {zipCodeHelperText}
                </FormHelperText>}
            </FormControl>
          </Stack>
        </Stack>

        <Stack direction='row' spacing={2} sx={{mt: 3, pb: isPhone ? 2 : 0, pr: isPhone ? 2 : 0, justifyContent: 'flex-end', width: 'inherit'}}>
          {/* sx={{ mt: '5rem' }} */}
          {saving ? (
            <PulseLoader color='#01674F' />
          ) : ''}
          <Button variant='outlined' color='primary' onClick={handleClose}>
            Cancel
          </Button>
          <Button type='submit' variant='contained' color='primary' disabled={saving}>
            Create
          </Button>
        </Stack>
      </form>
    </Dialog >
  );
};

export default CreateHerdDialog;
